import { createFocusTrap, FocusTrap } from 'focus-trap';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { on } from 'delegated-events';
import invisibleFocus from '../../../javascripts/utils/invisibleFocus';
import moveFocus from '../../../javascripts/utils/moveFocus';

const dialogs = new Map<HTMLElement, FocusTrap>();

const createOverlay = (
  $overlay: HTMLElement,
  destroyAfterClose = true,
  redirectAfterClose = false,
): FocusTrap => {
  const existingOverlay = dialogs.get($overlay);

  if (existingOverlay) {
    return existingOverlay;
  }

  const previouslyFocused = document.activeElement;
  const dialog = createFocusTrap($overlay, {
    initialFocus: false,
    returnFocusOnDeactivate: false,
    allowOutsideClick(event) {
      // Allow always clicks to cookie consent banner, which has always a higher z-index
      if (event.target instanceof HTMLElement && event.target.closest('.ccm-root')) {
        return true;
      }

      // Allow always clicks to Varify editor
      if (event.target instanceof HTMLElement && event.target.closest('#varify-shadow-root')) {
        return true;
      }

      return false;
    },
    onPostActivate() {
      // Send event
      $overlay.dispatchEvent(new Event('overlay:show', { bubbles: true }));

      // Scroll lock on overlay scroll container
      const $overlayScroll = $overlay.querySelector('.overlay__overlay');
      if ($overlayScroll) {
        disableBodyScroll($overlayScroll);
      }

      // Move focus
      moveFocus($overlay);
    },
    onDeactivate() {
      // Hide overlay
      $overlay.hidden = true;

      // Remove scroll lock
      const $overlayScroll = $overlay.querySelector('.overlay__overlay');
      if ($overlayScroll) {
        enableBodyScroll($overlayScroll);
      }

      // Make focus on source element invisible
      if (previouslyFocused instanceof HTMLElement) {
        if (document.contains(previouslyFocused)) {
          invisibleFocus(previouslyFocused);
        } else if (previouslyFocused.id) {
          const $replaced = document.getElementById(previouslyFocused.id);

          if ($replaced) {
            invisibleFocus($replaced);
          }
        }
      }

      // Set close url as new page URL
      const $closeLink =
        $overlay.querySelector<HTMLAnchorElement>('a.overlay__close');

      if ($closeLink) {
        window.history.replaceState(null, '', $closeLink.href);
      }

      // Send event
      $overlay.dispatchEvent(new Event('overlay:hide', { bubbles: true }));

      // Redirect after close
      if (redirectAfterClose && $closeLink) {
        document.location = $closeLink.href;
      }
    },
    onPostDeactivate() {
      // Destroy dialog!
      if (destroyAfterClose) {
        $overlay.dispatchEvent(new Event('overlay:destroy', { bubbles: true }));
        $overlay.remove();
        dialogs.delete($overlay);
      }
    },
  });


  // Save dialog for later
  dialogs.set($overlay, dialog);

  $overlay.addEventListener('overlay:scroll-to-top', () => {
    $overlay.querySelector('.overlay__overlay')?.scrollTo({ top: 0 });
  });

  $overlay.dispatchEvent(new Event('overlay:init', { bubbles: true }));

  if (!$overlay.hidden) {
    dialog.activate();
  }

  return dialog;
};

export const createOverlays = ($container: HTMLElement | Document) => {
  $container.querySelectorAll<HTMLElement>('.overlay').forEach(($overlay) => {
    createOverlay(
      $overlay,
      $overlay.getAttribute('data-destroy-after-close') === 'true',
      $overlay.getAttribute('data-redirect-after-close') === 'true',
    );
  });
};

createOverlays(document);

on('click', '.overlay__close, .overlay__backdrop', (event) => {
  const { currentTarget: $close } = event;

  event.preventDefault();

  const $overlay = $close.closest<HTMLElement>('.overlay');

  if ($overlay) {
    const existingOverlay = dialogs.get($overlay);
    existingOverlay?.deactivate();
  }
});

on('click', '[data-overlay]', (event) => {
  const { currentTarget: $trigger } = event;

  const $overlay = document.getElementById($trigger.dataset.overlay ?? '');

  if ($overlay) {
    event.preventDefault();
    $overlay.hidden = false;
    createOverlay($overlay, false).activate();
  }
});

window.addEventListener('lightbox:open', () => {
  dialogs.forEach((focusTrap) => {
    focusTrap.pause();
  });
});

window.addEventListener('lightbox:close', () => {
  dialogs.forEach((focusTrap) => {
    focusTrap.unpause();
  });
});

export default createOverlay;
